import { ButtonSecondary } from "components/Atoms/Button";
import SubLabel from "components/Atoms/Sublabel";
import { Dot } from "components/Dot";
import { FilterProvider, FilterProviderRef, FilterValues } from "components/filters/FilterContext";
import FilterHeader from "components/filters/FilterHeader";
import KeywordFilter from "components/filters/Filters/KeywordFilter";
import VisibilityFilter from "components/filters/Filters/VisibilityFilter";
import { mapKeywordFilterToInstruction, mapResponseToKeywordFilter, mapResponseToVisibilityFilter, mapVisibilityFilterToInstruction } from "components/filters/mappingUtils";
import { CustomSection } from "components/MainSectionTemplate/types";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import SmallDetails from "components/SmallDetails/SmallDetails";
import Table from "components/Tables/Table";
import React, { useEffect } from "react";
import { findCountryById } from "utilities/countrystateutils";
import { useManualPagination } from "utilities/hookutils";
import { BookkeepingConfigureModal } from "./BookkeepingConfigureModal";

interface Props {
  clientId: number;
}

const BookkeepingConfiguration = (props: Props) => {
  const filterRef = React.useRef<FilterProviderRef>(null);
  const maxPerPage = 10;
  const [isLoading, setIsLoading] = React.useState(true);
  const [bookkeeping, setBookkeeping] = React.useState<server.dto.BookkeepingForList[]>([]);
  const [configuringBookkeeping, setConfiguringBookkeeping] = React.useState<server.dto.BookkeepingForList | null>(null);

  const [instruction, setInstruction] = React.useState<server.dto.ListBookkeepingForAdminOneInstruction>({
    keyword: "",
    visible: "onlyVisible",
    clientId: props.clientId,
    page: 1,
    perPage: maxPerPage,
  });

  const fetchBookkeepings = async (instruction: server.dto.ListBookkeepingForAdminOneInstruction) => {
    setIsLoading(true);
    const res = await window.CoreApi.Bookkeeping.ListBookkeepingsForClient(instruction);
    setIsLoading(false);
    setBookkeeping(res.data.data ?? []);
    pagination.setPagination(res.data.pagination);

    filterRef.current.updateValues((_: FilterValues) => {
      return {
        keyword: mapResponseToKeywordFilter(res.data.request.keyword),
        visible: mapResponseToVisibilityFilter(res.data.request.visible),
      };
    });

    return res;
  }

  const onFilterChange = (filter: FilterValues) => {
    setInstruction(mapToInstruction(filter));
  };

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({
      ...instruction,
      page: paginationData.page,
      perPage: paginationData.perPage
    });
  };

  const pagination = useManualPagination(instruction.page || 1, maxPerPage, paginate);
  const isPaginationVisible = pagination.pageCount > 1;

  const mapToInstruction = (filter: FilterValues): server.dto.ListBookkeepingForAdminOneInstruction => {
    return {
      keyword: mapKeywordFilterToInstruction(filter.keyword),
      visible: mapVisibilityFilterToInstruction(filter.visible),
      clientId: props.clientId,
      page: 1,
      perPage: pagination.perPage,
    } satisfies server.dto.ListBookkeepingForAdminOneInstruction;
  };

  const isInitialRequest = React.useRef(true);
  useEffect(() => {
    fetchBookkeepings(instruction)
      .then(() => {
        if (!isInitialRequest.current) {
          return;
        }
        isInitialRequest.current = false;
        filterRef.current.setDefaultValues(() => {
          return {
            keyword: mapResponseToKeywordFilter(""),
            visible: mapResponseToVisibilityFilter("onlyVisible"),
          };
        });
      });
  }, [instruction]);

  return <>
    <BookkeepingConfigureModal
      isOpen={configuringBookkeeping !== null}
      onClose={(changed) => {
        if (changed) {
          fetchBookkeepings(instruction);
        }
        setConfiguringBookkeeping(null);
      }}
      bookkeepingId={configuringBookkeeping?.bookkeepingId}
    />
    <SectionPanel.Panel
      header={<>
        <PanelSegment.Title title="Bookkeeping" />
      </>}
    >
      <FilterProvider ref={filterRef} onFilterChange={onFilterChange}>
        <FilterHeader.Filters compact>
          <div className="selectiontool-header-filters">
            <KeywordFilter placeholder={RESX.GeneralLabels.Search} />
            <VisibilityFilter />
          </div>
        </FilterHeader.Filters>
      </FilterProvider>

      {bookkeeping.length > 0 &&
        <Table
          hideBottomPagination
          items={bookkeeping}
          isLoading={isLoading}
          columns={[{
            key: "visible",
            header: "",
            size: "fit-content(30px)",
            render: item => <div className="s1_cell_bigpadding">
              <Dot color={item.visible ? "green" : "red"} small={true} />
            </div>
          }, {
            key: "title",
            header: RESX.GeneralLabels.Title,
            render: item => <SmallDetails
              label={item.name}
              subLabel={item.code}
            />,
          }, {
            key: "exportMask",
            header: "Export mask",
            render: item => <>{serverReference.exportMasks.find(m => m.value === item.exportMask).name}</>
          }, {
            key: "locatedIn",
            header: RESX.Address.LocatedIn,
            render: item => {
              const country = findCountryById(item.companyAdmCountry);

              return <div>
                <div>{country.code}</div>
                {country.isEuMember && <SubLabel small>{RESX.VatNumber.InEU}</SubLabel>}
              </div>
            }
          }, {
            key: "storecove",
            header: "Storecove",
            render: item => <>{item.storecoveLegalEntityId}</>
          }, {
            key: "configure",
            header: "",
            render: item => <ButtonSecondary
              label={RESX.GeneralLabels.Configure}
              onClick={() => setConfiguringBookkeeping(item)}
            />,
            size: "fit-content(100px)"
          }]}
          getKey={item => `${item.bookkeepingId}`}
          pagination={isPaginationVisible && pagination.Component}
          canEdit={() => false}
          canDelete={() => false}
        />}
    </SectionPanel.Panel>
  </>
}

export const bookkeepingConfigurationSection = (clientId: number): CustomSection => {
  return {
    key: "bookkeepingConfiguration",
    type: "custom",
    render: () => <BookkeepingConfiguration
      key="bookkeepingConfiguration"
      clientId={clientId}
    />
  }
}